import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import type { RowAction } from "_shared/_views/tables/row-actions";
import Guid from "_shared/guids/guid";
import { type PermissionKind, useGetPermissionKindsQuery } from "permissions/_api/permission-kinds-api-service";
import {
  useAddPermissionTemplateMutation,
  useGetPermissionTemplateByIdQuery,
  useUpdatePermissionTemplateMutation,
} from "permissions/_api/permission-templates-api-service";
import GrantRow from "permissions/_views/grant-row/grant-row";
import PersonAreaSection from "permissions/_views/people/person-area-section";
import PermissionsSubjectHeader from "permissions/_views/scaffold/permissions-subject-header";
import {
  type TemplateKindRow,
  buildTemplateAreas,
  hiddenResourceIndexes,
  resourceIndex,
  templateRowModel,
  withKindGranted,
  withResourceLevel,
  withResourceSwitched,
  withoutResource,
  withoutResources,
} from "permissions/_views/templates/template-rows-model";
import { TEMPLATES_PATH, templatePath } from "permissions/_views/templates/templates-paths";
import type { Resource } from "permissions/resources/permission-resource";
import useSessionUser from "permissions/session/use-session-user";
import PermissionTemplate from "permissions/templates/permission-template";
import { type ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router";

/**
 * One template at `/permissions/templates/{id}`, and a new one at `/permissions/templates/new`.
 *
 * The template's kinds are drawn with grant rows, area by area: a switch on grants the kind at the
 * row's level, a switch off takes it away from whoever the template is applied to, and the kinds the
 * template does not name are folded under "Not in the template (n)" — applying it leaves those alone.
 * Nothing is saved until Save: a template is a draft until it is written, unlike a person's grants.
 *
 * A template belongs to the organization (#6826), so any administrator who manages member
 * permissions there opens and edits any of its templates; the server answers a stale save with 409.
 */

export const NEW_TEMPLATE_SEGMENT = "new";
export const NOT_IN_TEMPLATE = "Not in the template";

function counts(granted: number, takenAway: number): string {
  const parts = [`${granted} granted`];
  if (takenAway > 0) parts.push(`${takenAway} taken away`);
  return parts.join(" · ");
}

type AddTemplate = ReturnType<typeof useAddPermissionTemplateMutation>[0];
type UpdateTemplate = ReturnType<typeof useUpdatePermissionTemplateMutation>[0];

/**
 * Saves a template and says how it went. Answers the new template's id after a create, so the page
 * can move to its address. Module-level, so its `try` is not compiled into the component: the React
 * Compiler gives up on a whole component for a `??` inside one.
 */
async function writeTemplate(
  draft: PermissionTemplate,
  exists: boolean,
  addTemplate: AddTemplate,
  updateTemplate: UpdateTemplate,
): Promise<string | undefined> {
  try {
    if (exists) {
      await updateTemplate({ template: draft }).unwrap();
      enqueueSnackbar("Template updated", { variant: "success", role: "status" });
      return undefined;
    }
    const created = await addTemplate({ template: draft }).unwrap();
    enqueueSnackbar("Template created", { variant: "success", role: "status" });
    return created.id ?? draft.id.value;
  } catch (error) {
    enqueueSnackbar(getErrorMessageFromUnknown(error) ?? "The template could not be saved.", { variant: "error" });
    return undefined;
  }
}

function TemplateEditor(
  props: Readonly<{
    template?: PermissionTemplate;
    kinds: readonly PermissionKind[];
    areas: readonly string[];
  }>,
) {
  const { template, kinds, areas } = props;
  const navigate = useNavigate();
  const sessionUser = useSessionUser();
  const viewerUserId = sessionUser?.id ?? Guid.EMPTY;
  const mayAdministerPlatform = sessionUser?.canAdministerPlatform() ?? false;
  const [addTemplate] = useAddPermissionTemplateMutation();
  const [updateTemplate] = useUpdatePermissionTemplateMutation();

  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [resources, setResources] = useState<readonly Resource[]>(template?.resources ?? []);
  const [saving, setSaving] = useState(false);

  const sections = buildTemplateAreas(areas, kinds, resources, mayAdministerPlatform);
  const granted = sections.reduce((total, section) => total + section.grantedCount, 0);
  const takenAway = sections.reduce((total, section) => total + section.takenAwayCount, 0);
  const hidden = hiddenResourceIndexes(kinds, resources, mayAdministerPlatform);
  const title = template?.name ?? "New template";
  const rowName = name.trim() || title;

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || !sessionUser?.id) return;
    setSaving(true);
    const draft = new PermissionTemplate({
      id: template?.id ?? Guid.generate(),
      userId: template?.userId ?? sessionUser.id,
      resources: [...resources],
      name: trimmed,
      description: description.trim(),
      version: template?.version ?? 0,
    });
    const createdId = await writeTemplate(draft, template !== undefined, addTemplate, updateTemplate);
    setSaving(false);
    if (createdId) navigate(templatePath(createdId), { replace: true });
  };

  const renderRow = (row: TemplateKindRow) => {
    const model = templateRowModel(row, kinds, viewerUserId, mayAdministerPlatform);
    if (!row.resource) {
      return (
        <GrantRow
          key={row.key}
          row={model}
          subject="person"
          personName={rowName}
          onGrant={() => setResources(withKindGranted(resources, row.kind))}
        />
      );
    }
    const index = resourceIndex(row);
    const actions: RowAction[] = [
      {
        key: "leave-out",
        label: "Leave out of the template",
        ariaLabel: `Leave ${row.kind.name} out of ${rowName}`,
        icon: <RemoveCircleOutlineIcon />,
        onClick: () => setResources(withoutResource(resources, index)),
      },
    ];
    return (
      <GrantRow
        key={row.key}
        row={model}
        subject="person"
        personName={rowName}
        onSwitch={(on) => setResources(withResourceSwitched(resources, index, on))}
        onChangeLevel={(level) => setResources(withResourceLevel(resources, index, level))}
        actions={actions}
      />
    );
  };

  return (
    <Box sx={{ height: "100%", minHeight: 0, overflowY: "auto" }}>
      <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, md: 3 }, display: "grid", gap: 2 }}>
        <PermissionsSubjectHeader
          trail={[
            { label: "Permissions", to: "/permissions" },
            { label: "Templates", to: TEMPLATES_PATH },
          ]}
          title={title}
          avatar={<AutoFixHighIcon color="action" sx={{ fontSize: 40 }} />}
          secondary="Shared with your organization's administrators"
          actions={
            <>
              <Button variant="contained" disabled={!name.trim() || saving} onClick={() => void save()}>
                {template ? "Save" : "Create template"}
              </Button>
              <Button variant="outlined" onClick={() => navigate(TEMPLATES_PATH)}>
                Cancel
              </Button>
            </>
          }
        >
          <Typography variant="body2" color="text.secondary">
            {counts(granted, takenAway)}
          </Typography>
        </PermissionsSubjectHeader>
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { md: "1fr 2fr" } }}>
          <TextField label="Template name" required value={name} onChange={(event) => setName(event.target.value)} />
          <TextField label="Template description" value={description} onChange={(event) => setDescription(event.target.value)} />
        </Box>
        <Alert severity="info">
          {"Applying a template replaces a person's grants of every kind it names, granted or taken away. Kinds it does " +
            "not name are left as they are."}
        </Alert>
        {hidden.length > 0 && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => setResources(withoutResources(resources, hidden))}>
                Leave them out
              </Button>
            }
          >
            {`This template also names ${hidden.length === 1 ? "1 permission" : `${hidden.length} permissions`} this page cannot ` +
              "show: retired, per-record, or only a platform administrator's to grant. Applying the template still grants or " +
              "takes them away."}
          </Alert>
        )}
        {sections.map((section) => (
          <PersonAreaSection
            key={section.area}
            section={{ area: section.area, granted: section.included, notGranted: section.notIncluded }}
            counts={counts(section.grantedCount, section.takenAwayCount)}
            foldedLabel={NOT_IN_TEMPLATE}
            renderRow={renderRow}
          />
        ))}
      </Box>
    </Box>
  );
}

/** The route: gates as the templates list does, and waits for the template and the kinds. */
export default function TemplatePage() {
  const { templateId } = useParams();
  const sessionUser = useSessionUser();
  const kindsQuery = useGetPermissionKindsQuery();
  const isNew = templateId === NEW_TEMPLATE_SEGMENT;
  const templateQuery = useGetPermissionTemplateByIdQuery(!isNew && templateId ? { id: templateId } : skipToken);

  if (!sessionUser) return null;
  if (!sessionUser.canManagePermissionTemplates()) return <NotAuthorized />;

  let body: ReactNode;
  const failure = kindsQuery.error ?? templateQuery.error;
  if (failure) {
    body = (
      <Alert severity="error" sx={{ m: 2 }}>
        {`Unable to load the template. ${getErrorMessageFromUnknown(failure) ?? ""}`}
      </Alert>
    );
  } else if (!kindsQuery.data || (!isNew && !templateQuery.data)) {
    body = (
      <Box sx={{ p: 3, display: "grid", gap: 2 }} aria-busy="true">
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="rectangular" height={160} />
      </Box>
    );
  } else {
    const template = templateQuery.data ? PermissionTemplate.fromJSON(templateQuery.data) : undefined;
    body = (
      <TemplateEditor
        // Keyed by version too, so a save that the server answers with a new version starts the
        // editor afresh from it, and the next save sends that version.
        key={template ? `${template.id.value}-${template.version}` : NEW_TEMPLATE_SEGMENT}
        template={template}
        kinds={kindsQuery.data.kinds}
        areas={kindsQuery.data.areas}
      />
    );
  }
  return body;
}
